import React, { useContext } from 'react';
import {
  ProductTransactionExportConfiguration,
  ProductTransactionExportConfigurationField,
  ProductTransactionExportConfigurationInput,
} from '../../../../../../../types/product_transaction_export_configuration';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import NotFound from '../../../../../../components/NotFound/NotFound';
import { CustomFieldContext } from '../../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../../types/custom_field';
import ExportConfigurationForm, {
  DraggableExportFieldIdentifier,
} from '../../../../../../components/Export/ExportConfigurationForm';
import { translate } from '../../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';

interface ProductTransactionExportConfigurationMapping {
  value: string;
  index: number;
  field: ProductTransactionExportConfigurationField;
}

export interface DraggableProductTransactionExportFieldIdentifier
  extends DraggableExportFieldIdentifier<ProductTransactionExportConfigurationField>,
    ProductTransactionExportConfigurationMapping {}

interface ProductTransactionExportConfigurationFormProps {
  input: ProductTransactionExportConfigurationInput;
  setInput: React.Dispatch<React.SetStateAction<ProductTransactionExportConfigurationInput>>;
  footer: (
    configuration: ProductTransactionExportConfigurationInput,
    items: DraggableProductTransactionExportFieldIdentifier[],
  ) => JSX.Element;
  loading?: boolean;
}

export default function ProductTransactionExportConfigurationForm({
  input,
  setInput,
  footer,
  loading = false,
}: ProductTransactionExportConfigurationFormProps) {
  if (!input) return <NotFound />;

  const { customFields } = useContext(CustomFieldContext);

  const regularCustomFields = Array.from(customFields.values()).filter(
    item =>
      item.entityType === CustomFieldEntityType.productMasterData ||
      item.entityType === CustomFieldEntityType.productTransaction ||
      item.entityType === CustomFieldEntityType.lot,
  );
  const withFromAndToCustomFields = Array.from(customFields.values()).filter(
    item =>
      item.entityType === CustomFieldEntityType.product ||
      item.entityType === CustomFieldEntityType.bin ||
      item.entityType === CustomFieldEntityType.container,
  );

  const draggableItems: DraggableProductTransactionExportFieldIdentifier[] =
    DraggableItemData.forExportConfiguration<ProductTransactionExportConfigurationField>([
      ...Object.values(ProductTransactionExportConfigurationField).map(field => ({
        field,
        label: translate({ key: field }),
      })),
      ...regularCustomFields.map(item => ({ field: item.id, label: item.name })),
      ...withFromAndToCustomFields.map(item => ({
        field: `from::${item.id}`,
        label: `${item.name} (${translate({ key: TranslationKey.source })})`,
      })),
      ...withFromAndToCustomFields.map(item => ({
        field: `to::${item.id}`,
        label: `${item.name} (${translate({ key: TranslationKey.destination })})`,
      })),
    ]);

  return (
    <ExportConfigurationForm
      placeholder={'Add product transaction export field'}
      loading={loading}
      input={input}
      setInput={setInput}
      footer={footer}
      draggableItems={draggableItems}
      toDraggableItems={ProductTransactionExportConfiguration.toDraggableItems}
    />
  );
}
